import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PosthogService } from './posthog.service';

/**
 * Angular ErrorHandler that forwards exceptions to PostHog.
 * Adapted from https://posthog.com/docs/error-tracking/installation/angular
 * — capture is gated by analytics consent inside PosthogService.
 */
@Injectable()
export class PostHogErrorHandler implements ErrorHandler {
  constructor(private readonly posthog: PosthogService) {}

  handleError(error: unknown): void {
    const unwrapped = tryToUnwrapZonejsError(error);
    if (
      unwrapped instanceof HttpErrorResponse &&
      unwrapped.status > 0 &&
      unwrapped.status < 500
    ) {
      console.error(unwrapped);
      return;
    }

    const extractedError = this._extractError(error) ?? 'Unknown error';
    runOutsideAngular(() => this.posthog.captureException(extractedError));
    console.error(extractedError);
  }

  protected _extractError(errorCandidate: unknown): unknown {
    const error = tryToUnwrapZonejsError(errorCandidate);
    if (error instanceof HttpErrorResponse) {
      return extractHttpModuleError(error);
    }
    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error;
    }
    return null;
  }
}

function tryToUnwrapZonejsError(error: unknown): unknown {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function extractHttpModuleError(error: HttpErrorResponse): string | Error {
  if (isErrorOrErrorLikeObject(error.error)) {
    return error.error;
  }
  if (
    typeof ErrorEvent !== 'undefined' &&
    error.error instanceof ErrorEvent &&
    error.error.message
  ) {
    return error.error.message;
  }
  if (typeof error.error === 'string') {
    return `Server returned code ${error.status}`;
  }
  return error.message;
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) {
    return true;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return 'name' in value && 'message' in value && 'stack' in value;
}

declare const Zone: { root?: { run: <T>(fn: () => T) => T } } | undefined;
const isNgZoneEnabled = typeof Zone !== 'undefined' && Zone.root?.run;

export function runOutsideAngular<T>(callback: () => T): T {
  return isNgZoneEnabled ? Zone.root!.run(callback) : callback();
}

export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useClass: PostHogErrorHandler,
  };
}
